import React from 'react';
import { Document, Page, Text, View, StyleSheet, Image } from '@react-pdf/renderer';
import { DeliveryChallan } from '../types/billing';

// Revised styles matching the provided printed challan format
const styles = StyleSheet.create({
  page: { backgroundColor: '#FFFFFF', padding: 18, fontSize: 10, lineHeight: 1.4, fontFamily: 'Helvetica' },
  header: { marginBottom: 5 },
  headerImage: { width: '100%', maxHeight: 120, objectFit: 'contain', marginBottom: 5 },
  topHeader: { borderBottomWidth: 1, borderBottomColor: '#000', paddingBottom: 6, marginBottom: 6 },
  topHeaderRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start' },
  companyName: { fontSize: 16, fontWeight: 'bold' },
  companySubtitle: { fontSize: 10, fontWeight: 'bold' },
  companyDetails: { fontSize: 8, marginTop: 3 },
  challanRight: { alignItems: 'flex-end' },
  titleRibbon: { marginTop: 6, backgroundColor: '#b91c1c', color: '#fff', textAlign: 'center', paddingVertical: 4, fontWeight: 'bold', borderWidth: 1, borderColor: '#7f1d1d' },
  boxGrid: { borderWidth: 1, borderColor: '#000', marginTop: 6 },
  boxRow: { flexDirection: 'row', borderBottomWidth: 1, borderBottomColor: '#000' },
  cell: { padding: 6, borderRightWidth: 1, borderRightColor: '#000', fontSize: 9 },
  cellWide: { padding: 6, fontSize: 9 },
  label: { fontWeight: 'bold' },
  infoBox: { marginTop: 6 },
  infoText: { fontSize: 9 },
  table: { borderWidth: 1, borderColor: '#000', marginTop: 8 },
  tableHeader: { flexDirection: 'row', backgroundColor: '#e5e7eb', borderBottomWidth: 1, borderBottomColor: '#000' },
  th: { fontSize: 9, fontWeight: 'bold', padding: 6, borderRightWidth: 1, borderRightColor: '#000' },
  tr: { flexDirection: 'row' },
  td: { fontSize: 9, padding: 6, borderRightWidth: 1, borderRightColor: '#000', borderBottomWidth: 1, borderBottomColor: '#000' },
  colSN: { width: '10%', textAlign: 'center' },
  colDesc: { width: '55%' },
  colQty: { width: '15%', textAlign: 'center' },
  colRemark: { width: '20%' },
  notes: { borderWidth: 1, borderColor: '#000', marginTop: 8, padding: 6, fontSize: 9, backgroundColor: '#FFF3CD' },
  signatures: { flexDirection: 'row', justifyContent: 'space-between', marginTop: 20 },
  sigBlock: { width: '45%' },
  sigLine: { marginTop: 16, borderTopWidth: 1, borderTopColor: '#000', textAlign: 'center', paddingTop: 4, fontSize: 9 },
  footerNote: { marginTop: 18, textAlign: 'center', fontSize: 9 },
  watermark: { position: 'absolute', top: '55%', left: '50%', transform: 'translate(-50%, -50%) rotate(-45deg)', fontSize: 24, color: '#FF0000', opacity: 0.25, fontWeight: 'bold' },
});

interface DeliveryChallanPDFProps { challan: DeliveryChallan; }

// Updated company info for the header (AVIS Engineering & Consultancy)
const COMPANY_INFO = {
  name: "AVI'S ENGINEERING & CONSULTANCY",
  subtitle: 'NDT SERVICES',
  iso: 'An ISO 9001:2015 Certified Company',
  address:
    'SR No 4/1A, plot no: 17 SHREE SWAMI SAMARTH NIWAS, Bistabag, Saved, Ahilyanagar',
  phone: 'Phone: 7887635830, 7386156281',
  email: 'Email: avis.engineering25@gmail.com',
  website: 'Website: www.avisengineering.com',
};

const DeliveryChallanPDF: React.FC<DeliveryChallanPDFProps> = ({ challan }) => {
  // optional fields if present in data
  const wo = (challan as any)?.workOrderNumber || '';
  const transport = (challan as any)?.modeOfTransport || '';
  const vehicle = (challan as any)?.vehicleNumber || '';

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <Text style={styles.watermark}>NOT FOR SALE</Text>

        {/* Header Image (same as Quotation PDF) */}
        <View style={styles.header}>
          <Image src="/cpheader.png" style={styles.headerImage} />
        </View>

        {/* Company Header details and challan meta */}
        <View style={styles.topHeader}>
          <View style={styles.topHeaderRow}>
            <View>
              <Text style={styles.companyDetails}>{COMPANY_INFO.address}</Text>
              <Text style={styles.companyDetails}>{COMPANY_INFO.phone}</Text>
              <Text style={styles.companyDetails}>{COMPANY_INFO.email}</Text>
              <Text style={styles.companyDetails}>{COMPANY_INFO.website}</Text>
            </View>
            <View style={styles.challanRight}>
              <Text>Challan No. : {challan.challanNumber}</Text>
              <Text>Date : {new Date(challan.challanDate).toLocaleDateString('en-IN')}</Text>
            </View>
          </View>
          <Text style={styles.titleRibbon}>RETURNABLE DELIVERY CHALLAN</Text>
        </View>

        {/* Details grid */}
        <View style={styles.boxGrid}>
          <View style={styles.boxRow}>
            <Text style={[styles.cellWide, { flex: 1 }]}>
              <Text style={styles.label}>To: </Text>
              {challan.employeeName}
            </Text>
          </View>
          <View style={styles.boxRow}>
            <Text style={[styles.cell, { flex: 1 }]}>
              <Text style={styles.label}>Work Order No. & Date: </Text>
              {wo || '-'}
            </Text>
            <Text style={[styles.cellWide, { flex: 1 }]}>
              <Text style={styles.label}>Mode of Transport: </Text>
              {transport || '-'}
            </Text>
          </View>
          <View style={styles.boxRow}>
            <Text style={[styles.cellWide, { flex: 1 }]}>
              <Text style={styles.label}>Vehicle No.: </Text>
              {vehicle || '-'}
            </Text>
          </View>
        </View>

        {/* Info line */}
        <View style={styles.infoBox}>
          <Text style={styles.infoText}>Dear Sir, Kindly allow us to carry IN / OUT following Material</Text>
        </View>

        {/* Items table */}
        <View style={styles.table}>
          <View style={styles.tableHeader}>
            <Text style={[styles.th, styles.colSN]}>S.N.</Text>
            <Text style={[styles.th, styles.colDesc]}>Description of Material</Text>
            <Text style={[styles.th, styles.colQty]}>Qty</Text>
            <Text style={[styles.th, styles.colRemark]}>Remark</Text>
          </View>
          {challan.items.map((item, index) => (
            <View key={index} style={styles.tr}>
              <Text style={[styles.td, styles.colSN]}>{item.sn}</Text>
              <Text style={[styles.td, styles.colDesc]}>{item.description}</Text>
              <Text style={[styles.td, styles.colQty]}>{item.qty}</Text>
              <Text style={[styles.td, styles.colRemark]}>{item.remark || ''}</Text>
            </View>
          ))}
        </View>

        {challan.notes ? <Text style={styles.notes}>Notes: {challan.notes}</Text> : null}

        {/* Signature area */}
        <View style={styles.signatures}>
          <View style={styles.sigBlock}>
            <Text>Issued By</Text>
            <Text style={styles.sigLine}>{challan.issuedBy || ' '}</Text>
          </View>
          <View style={styles.sigBlock}>
            <Text>Received By</Text>
            <Text style={styles.sigLine}>{challan.receivedBy || ' '}</Text>
          </View>
        </View>

        <Text style={styles.footerNote}>For AVI'S ENGINEERING & CONSULTANCY{`\n`}Authorized Signatory</Text>
      </Page>
    </Document>
  );
};

export default DeliveryChallanPDF;
